import axios from 'axios';
import {getTokens, logout, NoCurrentUserError} from '@simpleinvest/front-end-auth';
import {HTTP_STATUS} from '@advisor/util/HttpStatus';
import {NOTIFICATION_TYPE, ENTERPRISE_FEATURES, SSO_REDIRECT_URI} from '@advisor/util/constants';
import exceptionHandler from '@advisor/util/exceptionHandler';
import {InvalidResponse} from '@advisor/util/customExceptions';
import {getAzureToken, validateToken} from './config/useAuth';

const APP_NAME = 'advisor-platform';
let store;
let router;
/**
 * @typedef {Object} Response
 * @property {Object} data
 */

/**
 * @typedef {Object} Error
 * @property {Number} status
 * @property {Response} response
 * @property {Object} config
 */

/**
 * @typedef {Object} RwToken
 * @property {String} access_token
 */

/**
 * This clears the storage and redirects to login page. mainly after token expires.
 * This appends currennt url to redirect, so that this will redirect to same page on login.
 * @param {Boolean?} withRedirectBack should it redirect back to origin page after login? default is true.
 */
export const redirectToLogin = async () => {
  try {
    await logout();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('redirectToLogin', error);
  } finally {
    localStorage.clear();

    let baseUrl = window.BASE_URL;

    // if SSO use the SSO URL
    const enterpriseFeaturesArray = store?.state?.settings?.enterprise?.features;

    if (Array.isArray(enterpriseFeaturesArray) && enterpriseFeaturesArray.includes(ENTERPRISE_FEATURES.SSO)) {
      baseUrl = baseUrl.replace(/\/?$/, '/');
      baseUrl += SSO_REDIRECT_URI;
    }

    window.location.href = baseUrl;
  }
};

/**
 * This method updates the session and sets the cognito tokens to the header
 * @param {*} config
 * @param {*} reThrow
 */
export const setCognitoHeader = async (config, getTokensFunc, reThrow = false) => {
  const configOption = config;

  try {
    let cognitoTokens = {};

    if (validateToken()) {
      cognitoTokens = getAzureToken();
    } else {
      cognitoTokens = await getTokensFunc();
    }

    const {accessToken, idToken} = cognitoTokens;

    if (accessToken) {
      // eslint-disable-next-line no-param-reassign
      configOption.headers['X-Cog-Authorization'] = `Bearer ${accessToken}`;
    }

    if (validateToken()) {
      configOption.headers.Authorization = `Bearer ${accessToken}`;
    } else if (idToken && !configOption.headers.Authorization) {
      // eslint-disable-next-line no-param-reassign
      configOption.headers.Authorization = `Bearer ${idToken}`;
    }
  } catch (error) {
    if (!(error instanceof NoCurrentUserError)) {
      exceptionHandler.handleGenericException('setCognitoHeader()', error);
    }

    if (reThrow) throw error;
  }
};

/**
 * This method sets cognito and rw tokens into their headers
 * @param {Object} config
 * @param {RwToken} tokenObj
 */
const setAuthHeaders = async (config) => {
  const configOption = config;

  await setCognitoHeader(configOption, getTokens);
};

/**
 * Add the access_token (if available) to each request
 * @param {Object} config
 *
 * @returns {Object}
 */
export const requestInterceptorFulfilledHandler = async (config) => {
  const configOption = {...config};

  await setAuthHeaders(configOption);
  /**
   * @type {Object}
   */
  const url = new URL(config.url);
  const params = new URLSearchParams(url.search);

  const isRiaSettingsEndpoint =
    url.pathname.includes('/riaFirm/') || url.pathname.includes('/riaLabelSettings/') || url.pathname.includes('/riaUrl');

  if (store.state.settings.default.riaComponent && !isRiaSettingsEndpoint) {
    params.set('riaComponent', store.state.settings.default.riaComponent);
  }

  url.search = params;
  configOption.url = url.href;
  configOption.headers['src-platform'] = APP_NAME;

  configOption.headers['src-location'] = window.location.href;

  if (router.currentRoute?.name) {
    configOption.headers['src-component'] = router.currentRoute.name;
  }

  return configOption;
};

export const requestInterceptorRejectedHandler = (error) => Promise.reject(error);

/**
 * This handles 403 status code
 * This will happen when someone tries to request information that isnt theirs
 * @param {Error} error
 */
const handleForbidden = (error) => {
  if (error.response.data.error && error.response.data.error.toLowerCase() === 'session expired') {
    localStorage.clear();
  }

  if (error?.config?.url && error.config.url.includes('https://advisor-cdn-v2')) {
    window.location.href = `${window.BASE_URL}/advisor/403`;

    return;
  }

  exceptionHandler.handleInterceptorException(HTTP_STATUS.FORBIDDEN.getReasonPhrase(), error);
};

/**
 * This handles 401 status code
 * @param {Error} error_
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const handleUnauthorized = async (error_) => {
  const error = error_;

  console.log(HTTP_STATUS.UNAUTHORIZED.getReasonPhrase(), error);
  await redirectToLogin();

  return Promise.reject(error);
};

/**
 * This handles 419 status code
 */
const handleAuthenticationTimeout = async () => {
  await redirectToLogin();
};

/**
 * This handles 500 status code
 * @param {Error} error
 */
const handleInternalServerError = (error) => {
  exceptionHandler.handleInternalServerError(HTTP_STATUS.INTERNAL_SERVER_ERROR.getReasonPhrase(), error);

  store.dispatch('notification/setMessage', {
    type: NOTIFICATION_TYPE.ERROR,
    text: 'Something went wrong.',
    timeout: 3,
  });
};

/**
 * This handles 400 status code
 * @param {Error} error
 */
const handleBadRequest = (error) => {
  exceptionHandler.handleInterceptorException(HTTP_STATUS.BAD_REQUEST.getReasonPhrase(), error);
};

/**
 * This handles 502 status code
 * @param {Error} error
 */
const handleBadGateway = (error) => {
  exceptionHandler.handleInterceptorException(HTTP_STATUS.BAD_GATEWAY.getReasonPhrase(), error);
};

/**
 * This handles 503 status code
 * @param {Error} error
 */
const handleServiceUnavailable = (error) => {
  exceptionHandler.handleInterceptorException(HTTP_STATUS.SERVICE_UNAVAILABLE.getReasonPhrase(), error);
};

/**
 * This handles 504 status code
 * @param {Error} error
 */
const handleGatewayTimeout = (error) => {
  exceptionHandler.handleInterceptorException(HTTP_STATUS.GATEWAY_TIMEOUT.getReasonPhrase(), error);
};

// eslint-disable-next-line complexity
export const responseInterceptorRejectedHandler = (error) => {
  if (error?.response) {
    switch (error.response.status) {
      case HTTP_STATUS.FORBIDDEN.getStatusCode(): {
        handleForbidden(error);
        break;
      }

      case HTTP_STATUS.UNAUTHORIZED.getStatusCode(): {
        return handleUnauthorized(error);
      }

      case HTTP_STATUS.AUTHENTICATION_TIMEOUT.getStatusCode(): {
        handleAuthenticationTimeout();
        break;
      }

      case HTTP_STATUS.INTERNAL_SERVER_ERROR.getStatusCode(): {
        handleInternalServerError(error);
        break;
      }

      case HTTP_STATUS.BAD_REQUEST.getStatusCode(): {
        handleBadRequest(error);
        break;
      }

      case HTTP_STATUS.BAD_GATEWAY.getStatusCode(): {
        handleBadGateway(error);
        break;
      }

      case HTTP_STATUS.SERVICE_UNAVAILABLE.getStatusCode(): {
        handleServiceUnavailable(error);
        break;
      }

      case HTTP_STATUS.GATEWAY_TIMEOUT.getStatusCode(): {
        handleGatewayTimeout(error);
        break;
      }

      default: {
        break;
      }
    }

    return Promise.reject(error);
  }

  return Promise.reject(new InvalidResponse(error));
};

export const responseInterceptorFulfilledHandler = (response) => response;

/**
 * This has called at the time bootstrapping of application
 * This handles request, response interceptors
 */
export const initialize = (_store, _router) => {
  store = _store;
  router = _router;
  axios.interceptors.response.use(responseInterceptorFulfilledHandler, responseInterceptorRejectedHandler);
  // Add the access_token (if available) to each request
  axios.interceptors.request.use(requestInterceptorFulfilledHandler, requestInterceptorRejectedHandler);
};
